import { useState } from 'react';
import axios from 'axios';
import { ArrowRight, Phone, Mail, MapPin, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import SEO from '@/components/SEO';
import { Reveal } from '@/lib/useReveal';
import { CONTACT_INFO } from '@/lib/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES_OPTIONS = [
  'SEO & AI Search', 'PPC & Paid Media', 'Web Design & Development',
  'Social Media', 'Branding & Creative', 'Full-Service Growth', 'Something Else',
];
const BUDGET_OPTIONS = ['Under $2.5K/mo', '$2.5K – $5K/mo', '$5K – $15K/mo', '$15K – $50K/mo', '$50K+/mo', 'Not sure yet'];

export default function ContactPage() {
  const [form, setForm] = useState({ name:'', email:'', company:'', phone:'', service:'', budget:'', message:'' });
  const [status, setStatus] = useState({ state:'idle', message:'' });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus({ state:'error', message:'Please fill in your name, email, and message.' });
      return;
    }
    setStatus({ state:'loading', message:'' });
    try {
      const res = await axios.post(`${API}/contact`, form);
      setStatus({ state:'success', message: res.data?.message || "Thanks - we'll be in touch within one business day." });
      setForm({ name:'', email:'', company:'', phone:'', service:'', budget:'', message:'' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Something went wrong. Please email hello@bp.agency directly.';
      setStatus({ state:'error', message: msg });
    }
  };

  return (
    <main data-testid="contact-page" className="bg-white">
      <SEO
        title="Contact - Book a Free Strategy Call"
        description="Tell us about your goals and we'll come back within one business day with a tailored next step. Strategy calls are free and confidential."
        path="/contact"
      />

      <section className="relative pt-14 lg:pt-20 pb-20 bg-radial-blue overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-40 pointer-events-none"/>
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 relative grid lg:grid-cols-12 gap-12 lg:gap-16">

          {/* Left column */}
          <div className="lg:col-span-5">
            <Reveal as="div" className="kicker"><span className="dot"/>Book a Free Strategy Call</Reveal>
            <Reveal as="h1" delay={80} className="h-display mt-5 text-[48px] sm:text-[64px] lg:text-[76px]">
              Let&rsquo;s talk <span className="grad-text">growth.</span>
            </Reveal>
            <Reveal as="p" delay={140} className="mt-6 text-[17px] text-bp-slate leading-relaxed max-w-md">
              Tell us where you are and where you&rsquo;re trying to go. We&rsquo;ll come back with a free
              audit, a recommendation, and a real conversation - no obligation, no pressure.
            </Reveal>

            <Reveal as="ul" delay={200} className="mt-10 space-y-4">
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl bg-bp-blue-50 text-bp-blue flex items-center justify-center flex-shrink-0"><Phone size={18}/></div>
                <div>
                  <div className="text-[12px] font-semibold uppercase tracking-wider text-bp-slate-soft">Call us</div>
                  <a href={`tel:${CONTACT_INFO.phoneTel}`} className="font-display font-bold text-bp-ink text-[18px] hover:text-bp-blue">{CONTACT_INFO.phone}</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl bg-bp-teal-50 text-bp-teal flex items-center justify-center flex-shrink-0"><Mail size={18}/></div>
                <div>
                  <div className="text-[12px] font-semibold uppercase tracking-wider text-bp-slate-soft">Email</div>
                  <a href={`mailto:${CONTACT_INFO.email}`} className="font-display font-bold text-bp-ink text-[18px] hover:text-bp-blue">{CONTACT_INFO.email}</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl bg-orange-50 text-bp-coral flex items-center justify-center flex-shrink-0"><MapPin size={18}/></div>
                <div>
                  <div className="text-[12px] font-semibold uppercase tracking-wider text-bp-slate-soft">Visit us</div>
                  <div className="font-display font-bold text-bp-ink text-[16px] sm:text-[17px] leading-tight">{CONTACT_INFO.addressLines[0]}</div>
                  <div className="text-[14px] text-bp-slate-soft mt-0.5">{CONTACT_INFO.addressLines[1]}</div>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl bg-bp-amber-50 text-bp-amber flex items-center justify-center flex-shrink-0"><Clock size={18}/></div>
                <div>
                  <div className="text-[12px] font-semibold uppercase tracking-wider text-bp-slate-soft">Office hours</div>
                  <div className="font-display font-bold text-bp-ink text-[18px]">Mon – Fri · 8:30a – 6p ET</div>
                  <div className="text-[13px] text-bp-slate-soft">Replies within one business day</div>
                </div>
              </li>
            </Reveal>
          </div>

          {/* Right column - Form */}
          <Reveal as="div" delay={120} className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="card-floating p-8 lg:p-10"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-x-5 gap-y-5">
                <div>
                  <label htmlFor="c-name" className="bp-label">Your name *</label>
                  <input id="c-name" data-testid="contact-name" required type="text" className="bp-field" value={form.name} onChange={update('name')} placeholder="Jane Smith"/>
                </div>
                <div>
                  <label htmlFor="c-email" className="bp-label">Email *</label>
                  <input id="c-email" data-testid="contact-email" required type="email" className="bp-field" value={form.email} onChange={update('email')} placeholder="jane@company.com"/>
                </div>
                <div>
                  <label htmlFor="c-company" className="bp-label">Company</label>
                  <input id="c-company" data-testid="contact-company" type="text" className="bp-field" value={form.company} onChange={update('company')} placeholder="Acme Inc."/>
                </div>
                <div>
                  <label htmlFor="c-phone" className="bp-label">Phone</label>
                  <input id="c-phone" data-testid="contact-phone" type="tel" className="bp-field" value={form.phone} onChange={update('phone')} placeholder="(555) 123-4567"/>
                </div>
                <div>
                  <label htmlFor="c-service" className="bp-label">Service interest</label>
                  <select id="c-service" data-testid="contact-service" className="bp-field" value={form.service} onChange={update('service')}>
                    <option value="">Select an option</option>
                    {SERVICES_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="c-budget" className="bp-label">Monthly budget</label>
                  <select id="c-budget" data-testid="contact-budget" className="bp-field" value={form.budget} onChange={update('budget')}>
                    <option value="">Select a range</option>
                    {BUDGET_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="c-message" className="bp-label">Tell us about your goals *</label>
                  <textarea
                    id="c-message"
                    data-testid="contact-message"
                    required
                    rows={5}
                    className="bp-field resize-none"
                    value={form.message}
                    onChange={update('message')}
                    placeholder="A few sentences about where you are, where you're trying to go, and any timelines we should know about…"
                  />
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-[12px] text-bp-slate-soft max-w-sm">
                  By submitting, you agree to be contacted by BrandPartners about your inquiry. We don&rsquo;t share your info - ever.
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={status.state === 'loading'}
                  className="btn-primary disabled:opacity-60"
                >
                  {status.state === 'loading' ? 'Sending…' : <>Send Message <ArrowRight size={16}/></>}
                </button>
              </div>

              {status.state === 'success' && (
                <div data-testid="contact-success" className="mt-6 flex items-start gap-3 p-4 rounded-2xl bg-bp-teal-50 border border-bp-teal/30">
                  <CheckCircle2 size={20} className="text-bp-teal flex-shrink-0 mt-0.5"/>
                  <div>
                    <div className="font-display font-bold text-bp-ink">Message received.</div>
                    <div className="text-[14px] text-bp-slate mt-1">{status.message}</div>
                  </div>
                </div>
              )}
              {status.state === 'error' && (
                <div data-testid="contact-error" className="mt-6 flex items-start gap-3 p-4 rounded-2xl bg-red-50 border border-red-200">
                  <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5"/>
                  <div className="text-[14px] text-bp-ink">{status.message}</div>
                </div>
              )}
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
